import { useLazyGetProductByBarcodeQuery } from "@/features/product";
import { useSelector } from "react-redux";
import { LIMIT } from "@/constants";
import { useGetPaymentMethodQuery } from "@/features/payment";
import { Product, SellerBuyerRootMapping, SellerProductDetail } from "@/interfaces";
import { PaymentMethodEnum } from "@/enum";
import { toast } from "react-toastify";
import CustomerModal from "@/features/customer/components";
import { debounce } from "lodash";
import { useExpenseOrderMutation } from "@/features/order";
import { useBarcodeScanner } from "@/hooks/useBarcode";
import { ProfileTypeEnum } from "@/enum/profileType.enum";
import { currentUserSelector } from "@/features/auth";
import { useGetCustomerListQuery } from "@/features/customer";
import { useGetSellerProductListQuery } from "@/features/seller";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { currencyFormatter, decimal, DecimalCalc } from "@/utils";
import { Button } from "@/components/ui/button";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";

import "./PurchaseCreate.css"
import { RouteComponent as AddProduct } from "../../../product/add-product";
import { X } from "lucide-react";
import { useTheme } from "@/design";
import { formDataOrderExpenseMapper } from "@/features/expense/mapper";

const userIcon = "/icons/user.png";

import { appColors } from "@/constants/color";
export const Route = createFileRoute('/(app)/(store)/expense/purchase/create/')(
  {
    component: RouteComponent,
  },
)

const initialValues = {
    subTotal: 0,
    productList: [],
    paymentMethod: PaymentMethodEnum.Cash,
}
function RouteComponent() {
    const {theme} = useTheme()
    const methods = useForm({
        defaultValues: initialValues,
    });
    const [createOrder , {isLoading}] = useExpenseOrderMutation();
    const navigate = useNavigate();
    const [showPrintModal, setShowPrintModal] = useState(false);

    const [isPaymentSummaryExpanded, setIsPaymentSummaryExpanded] =
    useState(false);
    const [isPaymentMethodExpanded, setIsPaymentMethodExpanded] = useState(true);

    const [showAddProductModal, setShowAddProductModal] = useState(false);
    const handleProductAdded = (newProductResponse: any) => {
      setShowAddProductModal(false);
      const currentItems = methods.watch("productList") || [];
      console.log({ currentItems });

      const productData = newProductResponse.data;
      const sellerDetail = productData.sellerProductDetail[0];

      const newProduct = {
        name: productData.name,
        taxPercentageList: productData.taxPercentageList,
        imageList: productData.imageList,
        ...sellerDetail, 
        productId: productData._id,
        quantity: sellerDetail.minimumQuantity?.quantity || 1,
        initialQuantity: sellerDetail.minimumQuantity?.quantity || 1,
      };

      methods.setValue("productList", [...currentItems, newProduct]);
    };
    const togglePaymentSummary = () => {
      setIsPaymentSummaryExpanded((prev) => !prev);
      if (isPaymentMethodExpanded) setIsPaymentMethodExpanded(false);
    };

    const togglePaymentMethod = () => {
      setIsPaymentMethodExpanded((prev) => !prev);
      if (isPaymentSummaryExpanded) setIsPaymentSummaryExpanded(false);
    };

    const onSubmit = async (data: any) => {
        try {
            const totalAmount = data.productList?.reduce((sum, item) => {
                return sum + (item.price.purchasePrice * item.quantity);
            }, 0) || 0;
            const dataWithTotal = {
                ...data,
                totalAmount: totalAmount,
            };

            const sellerType = data.venderDetails?.sellerType || ProfileTypeEnum.User;
            const payload = formDataOrderExpenseMapper(dataWithTotal);
            const response = await createOrder({ ...payload, sellerType }).unwrap();
            if (response.data) {
                toast.success("Order Created Successfully.");
                methods.reset(initialValues);
                navigate({
                    to: "/order/$orderId",
                    params: { orderId: response.data._id },
                });
            }
        } catch (error: any) {
            toast.error(error?.data.message ?? "Something went wrong.");
        }
    };
    const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
      }
    };
    // Print Confirmation Modal Component , 
    const PrintConfirmModal = () => (
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: appColors.modalOverlay,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div className="modal-content" style={{
          backgroundColor: appColors.surfaceBase,
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '400px',
          width: '90%',
          textAlign: 'center',
          boxShadow: `0 10px 25px ${appColors.shadowOverlay}`
        }}>
          <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
            Bill Generated Successfully!
          </h3>
          <p style={{ marginBottom: '24px', color: appColors.modalText, fontSize: '14px' }}>
            Would you like to print the bill now?
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <Button 
              variant="outline" 
              onClick={handlePrintSkip}
              style={{ minWidth: '100px' }}
            >
              Skip
            </Button>
            <Button 
              onClick={handlePrintConfirm}
              style={{ minWidth: '100px' }}
            >
              Print Bill
            </Button>
          </div>
        </div>
      </div>
    );


    return (
      <>
      <FormProvider {...methods}>
        <form
          className="generateBill"
          onSubmit={methods.handleSubmit(onSubmit)}
          onKeyDown={handleKeyDown}
        >
          <div className="billLeft">
            <CustomerSelector />
            <InvoiceItems setShowAddProductModal={setShowAddProductModal} />
          </div>
          <div className="billRight">
            <PaymentMethod
              isExpanded={isPaymentMethodExpanded}
              toggleExpanded={togglePaymentMethod}
            />
            <PaymentSummary
              isExpanded={isPaymentSummaryExpanded}
              toggleExpanded={togglePaymentSummary}
            />
          </div>
          <div className="buttonRow generateBtn">
            <Button
              type="submit"
              size="xl"
              className="Generate"
              disabled={isLoading}
            >
              Generate Bill
            </Button>
          </div>
        </form>
      </FormProvider>
      {showPrintModal && <PrintConfirmModal />}
        {showAddProductModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: appColors.modalOverlay,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div 
              className="addProductModal"
              style={{
                maxWidth: "640px",
                width: "100%",
                maxHeight: "90vh",
                backgroundColor: appColors.surfaceBase,
                borderRadius: "12px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div 
                className="addProductModalHeader"
                style={{
                  padding: "20px 24px",
                  borderBottom: `1px solid ${theme.colors.border}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: theme.colors.background,
                  flexShrink: 0,
                }}
              >
                <h3 style={{ margin: 0, fontSize: "20px", fontWeight: "600" }}>
                  Add Product
                </h3>
                <button
                  onClick={() => setShowAddProductModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    cursor: "pointer",
                    width: "32px",
                    height: "32px",
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <X/>
                </button>
              </div>
              <div 
                className="addProductModalContent"
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "0",
                }}
              >
                <AddProduct
                  onSuccess={handleProductAdded}
                  onClose={() => setShowAddProductModal(false)}
                />
              </div>
            </div>
          </div>
        )}
      </>
    );
}

function CustomerSelector() {
    const { setValue, watch } = useFormContext();
    const selectedCustomer = watch("venderDetails");
    const searchInputRef = useRef<HTMLInputElement>(null)
    const [openCustomerModal, setOpenCustomerModal] = useState<boolean>(false);
    const [query, setQuery] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { data: customers = [] } = useGetCustomerListQuery({
        seller: true,
        page: 1,
        limit: 10,
        search: query,
    });
    // Handle clicks outside the dropdown
    const handleClickOutside = useCallback((event: MouseEvent) => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target as Node)
        ) {
            setShowSuggestions(false);
        }
    }, []);

    useMemo(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [handleClickOutside]);

    const handleCustomerSelect = (customer: SellerBuyerRootMapping) => {
        setValue("venderDetails", customer);
        if (searchInputRef.current) {
            searchInputRef.current.value = customer.sellerName as string;
        };
        setShowSuggestions(false);
    };
    const debouncedSetQuery = useCallback(
        debounce((val: string) => setQuery(val), 300),
        []
    );

    const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        debouncedSetQuery(event.target.value);
        if (!showSuggestions) {
            setShowSuggestions(true);
        }
    }, [showSuggestions, debouncedSetQuery])
    const renderCustomer = ({
        customer,
    }: {
        customer: SellerBuyerRootMapping;
    }) => {
        return (
            <div
                className="dropdownItem"
                onClick={() => handleCustomerSelect(customer)}
            >
                <div className="customerInfo">
                    <strong>{customer?.sellerName}</strong>
                    <span>{customer?.sellerPhoneNumber}</span>
                </div>
            </div>
        );
    };
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowSuggestions(false); // <-- trigger close
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showSuggestions]);

    return (
        <div className="customerCard">
            <div className="itemsHeader">
                <h3 className="containerHeading">Vender</h3>
                <button type="button" className="addItemsButton" onClick={() => setOpenCustomerModal(true)}>
                    + Add New Vender
                </button>
                <CustomerModal isOpen={openCustomerModal} onClose={() => setOpenCustomerModal(false)} clientType="Seller" onCustomerCreated={handleCustomerSelect} />
            </div>
            <div className="customerInputContainer">
                <div className="product-dropdown" style={{ width: "100%" }}>
                    <input
                        ref={searchInputRef}
                        className="customerInput"
                        type="text"
                        placeholder="Enter or Search for customer"
                        onChange={handleChange}
                    />
                    <img src={userIcon} alt="User" className="customerInputIcon" />

                    {showSuggestions && (
                        <div className="dropdownCard" ref={dropdownRef}>
                            {customers?.length > 0 ? (
                                customers?.map((customer) => renderCustomer({ customer: customer }))
                            ) : (
                                <div className="dropdown-empty">No vender found</div>
                            )}
                        </div>
                    )}
                </div>

            </div>
            <div className="customerTags">
                {customers.slice(0, 6).map((customer) => (
                    <span
                        key={customer._id}
                        className={`customerTag ${selectedCustomer === customer.buyerName ? "customerTagSelected" : ""}`}
                        onClick={() => handleCustomerSelect(customer)}
                    >
                        {customer?.sellerName}
                    </span>
                ))}
            </div>
        </div>
    );
}
function InvoiceItems({setShowAddProductModal}) {
    const currentUser = useSelector(currentUserSelector);
    const { watch, setValue, register, control } = useFormContext();
    const [query, setQuery] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [page] = useState(1);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [showPurchasePrice, setShowPurchasePrice] = useState(false);
    const canViewPurchasePrice = currentUser?.profileDetail?.saleOrderConfig?.showPurchasePrice ?? true;
    const [activeIndex, setActiveIndex] = useState(-1);
    const itemsRef = useRef<HTMLDivElement[]>([]);
    const {
        data: productList=[],
        isLoading,
        error,
    } = useGetSellerProductListQuery({
        search: query,
        limit: LIMIT * 2,
        page,
    });
    const items = watch("productList") || [];

    // Handle clicks outside the dropdown
    const handleClickOutside = useCallback((event: MouseEvent) => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target as Node)
        ) {
            setShowSuggestions(false);
            setActiveIndex(-1);
        }
    }, []);

    // Set up event listener for outsi
    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);
    const [getProductByBarcode] = useLazyGetProductByBarcodeQuery()

    useBarcodeScanner(async barcode => {
        const resposne = await getProductByBarcode({ barcode }).unwrap()
        if (resposne.data) {
            handleSelect(({
                name: resposne.data.name,
                taxPercentageList: resposne.data.taxPercentageList,
                imageList: resposne.data.imageList,
                ...resposne.data?.sellerProductDetail?.at(0),
                quantity:
                    resposne.data?.sellerProductDetail?.at(0)?.minimumQuantity?.quantity ?? 1,
            }))
        }
    })


    const updateQty = (index: number, delta: number) => {
        const updated = [...items];
        const currentQty = updated[index].quantity;
        const initialQty = updated[index].initialQuantity || 1;

        if (currentQty + delta < initialQty) {
            updated.splice(index, 1);
        } else {
            updated[index].quantity = Math.max(initialQty, Number(currentQty) + delta);
        }
        setValue("productList", updated);
    };

    const removeItem = (index: number) => {
        const updated = [...items];
        updated.splice(index, 1);
        setValue("productList", updated);
    };

    const handlePriceChange = (
        index: number,
        field: "mrp" | "sellingPrice",
        value: number
    ) => {
        const updated = [...items];
        updated[index]["price"][field] = value;
        setValue("productList", updated);
    };

    const handleSelect = (product: any) => {
        const existingItemIndex = items.findIndex(
            (item: any) => item._id === product._id
        );
        const initialQuantity = product.quantity;
        if (existingItemIndex > -1) {
            const updatedItems = [...items];
            updatedItems[existingItemIndex].quantity += 1; // Increment quantity if item already exists
            setValue("productList", updatedItems);
        } else {
            setValue("productList", [...items, { ...product, initialQuantity }]);
        }
        setShowSuggestions(false);
        setQuery("");
        setActiveIndex(-1);
    };

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
          setShowSuggestions(false);
          setActiveIndex(-1);
        } else if (showSuggestions && productList && productList.length > 0) {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((prev) =>
              prev < productList.length - 1 ? prev + 1 : 0
            );
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((prev) =>
              prev > 0 ? prev - 1 : productList.length - 1
            );
          } else if (e.key === "Enter" && activeIndex >= 0) {
            e.preventDefault();
            const selectedProduct = productList[activeIndex];
            handleSelect({
              name: selectedProduct.name,
              taxPercentageList: selectedProduct.taxPercentageList,
              imageList: selectedProduct.imageList,
              ...selectedProduct?.sellerProductDetail?.at(0),
              quantity:
                selectedProduct?.sellerProductDetail?.at(0)?.minimumQuantity
                  ?.quantity ?? 1,
            });
            setQuery("");
          }
        }
      },
      [showSuggestions, productList, activeIndex]
    );

    const debouncedSetQuery = useCallback(
        debounce((val: string) => setQuery(val), 300),
        []
    );
    const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        debouncedSetQuery(event.target.value);
        if (!showSuggestions) {
            setShowSuggestions(true);
        }
        setActiveIndex(-1);
    }, [showSuggestions, debouncedSetQuery])
    const handleFoucus = useCallback(() => {
        setQuery('');
        if (!showSuggestions) {
            setShowSuggestions(true);
        }
        setActiveIndex(-1);
    }, [showSuggestions])

    const handleQuantityChange = useCallback((event: ChangeEvent<HTMLInputElement>, index: number, cb: Function) => {
        const initialQty = items[index].minimumQuantity?.quantity || 1;
        if (Number(event.target.value) < initialQty) {
            cb(initialQty);
        } else {
            cb(event)
        }
    }, [])

    const renderItem = ({ item, index }: { item: any; index: number }) => {
        if (item?.sellerProductDetail?.length > 1) {
            return item.sellerProductDetail.map(
                (sellerProductDetail: SellerProductDetail, detailIndex: number) =>
                    renderItem({
                        item: {
                            ...item,
                            sellerProductDetail: [{ ...sellerProductDetail }],
                        },
                        index: index + detailIndex
                    } as any)
            );
        }
        // if (
        //     selectedProductList?.has(
        //         item?.sellerProductDetail?.at(0)?._id + item._id,
        //     )
        // ) {
        //     return null;
        // }
        return (
          <div
            ref={el => itemsRef.current[index] = el!}
            className={`dropdownItem ${index === activeIndex ? 'active' : ''}`}
            onClick={() =>
              handleSelect({
                name: item.name,
                taxPercentageList: item.taxPercentageList,
                imageList: item.imageList,
                ...item?.sellerProductDetail?.at(0),
                quantity:
                  item?.sellerProductDetail?.at(0)?.minimumQuantity?.quantity ??
                  1,
              } as any)
            }
            onMouseEnter={() => setActiveIndex(index)}
          >
            <img
              src={item.imageList?.at(0)}
              alt={item.name}
              className="dropDownImage"
            />
            <div className="productDropDownContainer">
              <strong>{item.name}</strong>
              <div className="productDetails">
                {item?.sellerProductDetail?.at(0)?.price?.mrp?.toFixed(2) && (
                  <span>
                    MRP: ₹
                    {item?.sellerProductDetail?.at(0)?.price?.mrp?.toFixed(2)}
                  </span>
                )}

                {item?.sellerProductDetail
                  ?.at(0)
                  ?.price?.sellingPrice?.toFixed(2) && (
                  <span>
                    Selling: ₹
                    {item?.sellerProductDetail
                      ?.at(0)
                      ?.price?.sellingPrice?.toFixed(2)}
                  </span>
                )}

                {item?.sellerProductDetail?.at(0)?.barcode && (
                  <span>
                    barcode: {item?.sellerProductDetail?.at(0)?.barcode}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
    };

    const handleToggle = useCallback(() => {
        setShowPurchasePrice(pre => !pre)
    }, [])
    return (
        <div className="itemsCard">
            <div className="itemsHeader">
                <h3 className="containerHeading">Invoice Items</h3>
                <Button
                  type="button"
                  variant="link"
                  // to="/product/add-product"
                  onClick={()=> setShowAddProductModal(true)}
                  className="addItemsButton"
                >
                  + Add New Product
                </Button>
            </div>
            <div className="productDropdown" ref={dropdownRef}>
                <input
                    type="text"
                    placeholder="Search products..."
                    onChange={handleChange}
                    onFocus={handleFoucus}
                    onKeyDown={handleKeyDown}
                    style={{ width: canViewPurchasePrice ? 'calc(100% - 40px)' : '100%' }}
                />
                {canViewPurchasePrice && (
                  <button
                    className="purchasePrice"
                    onClick={() => setShowPurchasePrice(!showPurchasePrice)}
                    type="button"
                    style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <p style={{ margin: 0, color: appColors.accentBlue, fontSize: '14px', fontWeight: 500 }}>Purchase Price</p>
                    {showPurchasePrice ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 20 20" fill="none">
                        <path d="M9.99967 7.5C10.6627 7.5 11.2986 7.76339 11.7674 8.23223C12.2363 8.70107 12.4997 9.33696 12.4997 10C12.4997 10.663 12.2363 11.2989 11.7674 11.7678C11.2986 12.2366 10.6627 12.5 9.99967 12.5C9.33663 12.5 8.70075 12.2366 8.23191 11.7678C7.76307 11.2989 7.49967 10.663 7.49967 10C7.49967 9.33696 7.76307 8.70107 8.23191 8.23223C8.70075 7.76339 9.33663 7.5 9.99967 7.5ZM9.99967 3.75C14.1663 3.75 17.7247 6.34167 19.1663 10C17.7247 13.6583 14.1663 16.25 9.99967 16.25C5.83301 16.25 2.27467 13.6583 0.833008 10C2.27467 6.34167 5.83301 3.75 9.99967 3.75ZM2.64967 10C3.32322 11.3753 4.3691 12.534 5.66841 13.3444C6.96772 14.1548 8.46834 14.5844 9.99967 14.5844C11.531 14.5844 13.0316 14.1548 14.3309 13.3444C15.6303 12.534 16.6761 11.3753 17.3497 10C16.6761 8.62474 15.6303 7.46604 14.3309 6.65562C13.0316 5.8452 11.531 5.41557 9.99967 5.41557C8.46834 5.41557 6.96772 5.8452 5.66841 6.65562C4.3691 7.46604 3.32322 8.62474 2.64967 10Z" fill={appColors.accentBlue} />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 20 20" fill="none">
                        <path d="M1.66634 4.39159L2.73301 3.33325L16.6663 17.2666L15.608 18.3333L13.0413 15.7666C12.083 16.0833 11.0663 16.2499 9.99967 16.2499C5.83301 16.2499 2.27467 13.6583 0.833008 9.99992C1.40801 8.53325 2.32467 7.24159 3.49134 6.21659L1.66634 4.39159ZM9.99967 7.49992C10.6627 7.49992 11.2986 7.76331 11.7674 8.23215C12.2363 8.70099 12.4997 9.33688 12.4997 9.99992C12.5001 10.2837 12.4522 10.5655 12.358 10.8333L9.16634 7.64159C9.43406 7.5474 9.71587 7.4995 9.99967 7.49992ZM9.99967 3.74992C14.1663 3.74992 17.7247 6.34159 19.1663 9.99992C18.4858 11.7274 17.3301 13.2268 15.833 14.3249L14.6497 13.1333C15.8021 12.3361 16.7316 11.2575 17.3497 9.99992C16.6761 8.62481 15.6301 7.46628 14.3308 6.65605C13.0315 5.84581 11.5309 5.41639 9.99967 5.41659C9.09134 5.41659 8.19968 5.56659 7.36634 5.83325L6.08301 4.55825C7.28301 4.04159 8.60801 3.74992 9.99967 3.74992ZM2.64967 9.99992C3.3233 11.375 4.36922 12.5336 5.66854 13.3438C6.96785 14.154 8.46843 14.5834 9.99967 14.5833C10.5747 14.5833 11.1413 14.5249 11.6663 14.4083L9.76634 12.4999C9.18648 12.4378 8.64536 12.179 8.23299 11.7666C7.82061 11.3542 7.56183 10.8131 7.49967 10.2333L4.66634 7.39158C3.84134 8.09992 3.14967 8.98325 2.64967 9.99992Z" fill={appColors.accentBlue} />
                      </svg>
                    )}
                  </button>
                )}

                {showSuggestions && (
                    <div className="dropdownCard">
                        {isLoading ? (
                            <div className="dropdownLoading">Searching products to add in invoice...</div>
                        ) : error ? (
                            <div className="dropdownError">Failed to load products</div>
                        ) : productList && productList.length > 0 ? (
                            productList.map((product,  index) => renderItem({ item: product, index  }))
                        ) : query ? (
                            <div className="dropdownEmpty">No products found</div>
                        ) : (
                            <div className="dropdownEmpty">
                                Start typing to search products
                            </div>
                        )}
                    </div>
                )}
            </div>
            {items.map(
                (
                    item: Product &
                        SellerProductDetail & { quantity: number; initialQuantity: number },
                    i: number
                ) => {
                    const isAtInitialQty = item.quantity === (item.minimumQuantity?.quantity || 1);

                    return (
                        <div className="invoiceItem" key={item._id}>
                            <div className="itemDetails">
                                <div className="itemImageContainer">
                                    <img
                                        src={
                                            item?.imageList?.at(0) || "https://via.placeholder.com/50"
                                        }
                                        alt="item"
                                        className="itemImage"
                                    />
                                </div>
                                <div className="itemInfo">
                                    <p className="itemName">{item.name}</p>
                                    <section>
                                        <p className="itemPrice">
                                            {item?.quantity} X {" "}{currencyFormatter(item?.price?.sellingPrice)}
                                        </p>
                                        {showPurchasePrice && <p className="itemPrice">
                                            Purchase Price {currencyFormatter(item.price.purchasePrice)}
                                        </p>}
                                    </section>
                                </div>
                            </div>

                        <div className="itemControls">
                          <div className="quantityControls">
                            {isAtInitialQty ? (
                              <button
                                type="button"
                                className="quantityButton"
                                style={{ color: appColors.dangerRed }}
                                onClick={() => removeItem(i)}
                                title="Remove item"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  className="dustbinIcon"
                                >
                                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="quantityButton"
                                onClick={() => updateQty(i, -1)}
                              >
                                -
                              </button>
                            )}
                            <Controller
                              control={control}
                              name={`productList.${i}.quantity`}
                              render={({
                                field: { onBlur, onChange, value },
                                fieldState: { error },
                              }) => (
                                <input
                                  type="number"
                                  className={`quantityInput ${error ? "quantityInputError" : ""}`}
                                  value={value}
                                  onBlur={onBlur}
                                  onChange={(event) =>
                                    handleQuantityChange(event, i, onChange)
                                  }
                                />
                              )}
                            />
                            {/* <span className="quantityValue">{String(item.quantity).padStart(2,0)}</span> */}
                            <button
                              type="button"
                              className="quantityButton"
                              onClick={() => updateQty(i, 1)}
                            >
                              +
                            </button>
                          </div>
                          <div className="priceDisplay">
                            <p className="priceLabel">MRP</p>
                            <input
                              type="number"
                              className="priceInput"
                              value={item?.price?.mrp}
                              onChange={(e) =>
                                handlePriceChange(
                                  i,
                                  "mrp",
                                  Number(e.target.value)
                                )
                              }
                              min="0"
                            />  
                          </div>
                          <div className="priceDisplay">
                            <p className="priceLabel">Purchase</p>
                            <Controller
                              name={`productList.${i}.price.purchasePrice`}
                              control={control}
                              render={({ field }) => (
                                <input
                                  {...field}
                                  type="number"
                                  className="priceInput"
                                  min="0"
                                  step="0.1"
                                />
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    );
                }
            )}
        </div>
    );
}



const PaymentMethod = ({ isExpanded, toggleExpanded }) => {
  const { register, watch, setValue } = useFormContext();
  const { data: accounts = [] } = useGetPaymentMethodQuery({});
  const [defaultMethodSet, setDefaultMethodSet] = useState(false);
  const [isSplitActive, setIsSplitActive] = useState(false);
  const selectedPaymentMethods = watch("paymentMethods") || [];
  const selectedMethod = watch("paymentMethod");
  const splitAmounts = watch("splitAmounts") || {};

  useEffect(() => {
      if (accounts.length > 0 && !defaultMethodSet) {
        const savedMethod = localStorage.getItem("create-invoice-paymentMethod");
  
        if (savedMethod) {
          // Check if saved method is a valid account ID or enum value
          const isValidAccount = accounts.some((acc) => acc._id === savedMethod);
          const isValidEnum = Object.values(PaymentMethodEnum).includes(
            savedMethod as PaymentMethodEnum
          );
  
          if (isValidAccount || isValidEnum) {
            setValue("paymentMethod", savedMethod);
            setValue("paymentMethods", [savedMethod]);
          } else {
            // Fallback to first UPI or cash
            const firstUpiAccount = accounts.find((acc) => acc.upi?.upiId);
            const defaultMethod = firstUpiAccount
              ? firstUpiAccount._id
              : PaymentMethodEnum.Cash;
            setValue("paymentMethod", defaultMethod);
            setValue("paymentMethods", [defaultMethod]);
            localStorage.setItem("create-invoice-paymentMethod", defaultMethod);
          }
        } else {
          // Set default if no saved method
          const firstUpiAccount = accounts.find((acc) => acc.upi?.upiId);
          const defaultMethod = firstUpiAccount
            ? firstUpiAccount._id
            : PaymentMethodEnum.Cash;
          setValue("paymentMethod", defaultMethod);
          setValue("paymentMethods", [defaultMethod]);
          localStorage.setItem("create-invoice-paymentMethod", defaultMethod);
        }
  
        setDefaultMethodSet(true);
      }
    }, [accounts, defaultMethodSet, setValue]);
  
    // Automatically select/deselect methods based on split amount input
    useEffect(() => {
      if (isSplitActive) {
        const currentMethods = selectedPaymentMethods;
        const updatedMethods = [];
        
        // Check all payment methods
        const allMethods = [
          ...accounts.map(acc => acc._id),
          ...Object.values(PaymentMethodEnum)
        ];
        
        allMethods.forEach(methodId => {
          const amount = parseFloat(splitAmounts[methodId] || 0);
          
          if (amount > 0) {
            // Add to selection if amount > 0
            updatedMethods.push(methodId);
          }
        });
        
        // Update payment methods if changed
        if (JSON.stringify(updatedMethods.sort()) !== JSON.stringify(currentMethods.sort())) {
          setValue("paymentMethods", updatedMethods);
        }
      }
    }, [splitAmounts, isSplitActive, setValue]);

  

  const activateSplitPayment = () => {
    setIsSplitActive(true);
    // Initialize split amounts when activating split payment
    const initialSplitAmounts = {};
    accounts.forEach((account) => {
      initialSplitAmounts[account._id] = "";
    });
    Object.values(PaymentMethodEnum).forEach((method) => {
      initialSplitAmounts[method] = "";
    });
    setValue("splitAmounts", initialSplitAmounts);
    setValue("paymentMethods", []);
  };

  const deactivateSplitPayment = () => {
    setIsSplitActive(false);
    // Clear split amounts when deactivating split payment
    setValue("splitAmounts", {});
    // Reset to single selection mode
    if (selectedPaymentMethods.length > 0) {
      setValue("paymentMethod", selectedPaymentMethods[0]);
    }
  };

  const handleMethodSelection = (methodId) => {
    if (!isSplitActive) {
      const methodToStore = accounts.find((acc) => acc._id === methodId)
        ? methodId // Store account ID for UPI
        : methodId; //
      // Single selection mode - update both paymentMethod and paymentMethods
      setValue("paymentMethod", methodId);
      setValue("paymentMethods", [methodId]);
      localStorage.setItem("create-invoice-paymentMethod", methodToStore);
    }
    // In split mode, selection is handled automatically by amount input
  };
  const getDisplayMethod = () => {
      if (isSplitActive) {
        if (selectedPaymentMethods.length === 0) return "";
  
        const methodNames = selectedPaymentMethods.map((methodId) => {
          // Check if it's a UPI account
          const account = accounts.find((acc) => acc._id === methodId);
          if (account) {
            return `${account.name} (${account.upi?.upiId || "UPI"})`;
          }
  
          // Handle enum payment methods
          switch (methodId) {
            case PaymentMethodEnum.Cash:
              return "Cash";
            case PaymentMethodEnum.Card:
              return "Card";
            case PaymentMethodEnum.Other:
              return "Other";
            default:
              return "";
          }
        });
  
        return methodNames.join(", ");
      } else {
        // For single selection
        if (selectedMethod === PaymentMethodEnum.Cash) return "Cash";
        if (selectedMethod === PaymentMethodEnum.Card) return "Card";
        if (selectedMethod === PaymentMethodEnum.Other) return "Other";
  
        // For UPI accounts
        const selectedAccount = accounts.find(
          (acc) => acc._id === selectedMethod
        );
        return selectedAccount
          ? selectedAccount.upi?.upiId || "UPI"
          : "";
      }
  };
  

  const isMethodSelected = (methodId) => {
    if (!isSplitActive) {
      return selectedMethod === methodId;
    }
    return (watch("paymentMethods") || []).includes(methodId);
  };

  return (
    <div
      className={`paymentMethodContainer ${isExpanded ? "expanded" : "collapsed"}`}
    >
      <div className="paymentMethodHeader" onClick={toggleExpanded}>
        <h3 className="containerHeading">Payment Method</h3>
        <div className="paymentMethodSummary">
          {getDisplayMethod()} 
        </div>
      </div>
      {isExpanded && (
        <>
          <div className="splitContainer">
            <button
              className={`splitToggle ${!isSplitActive ? "active" : ""}`}
              onClick={deactivateSplitPayment}
              type="button"
            >
              Payment
            </button>
            <button
              type="button"
              className={`splitToggle ${isSplitActive ? "active" : ""}`}
              onClick={activateSplitPayment}
            >
              <span>Split</span>
            </button>
          </div>

          <h4 className="paymentSubheading">UPI</h4>
          <div className="paymentMethodCard">
            {accounts?.map((bank) => (
              <div
                key={bank._id}
                className={`bankOption ${isMethodSelected(bank._id) ? "selected" : ""}`}
              >
                <label className="bankLabel">
                  <div className="bankInfo">
                    <img
                      src={bank.logoURL}
                      alt={bank.name}
                      className="bankLogo"
                    />
                    <div className="bankText">
                      <span className="bankName">{bank.name}</span>
                      <span className="bankDetails">{bank.upi?.upiId}</span>
                    </div>
                  </div>
                  <div className="paymentRightSection">
                    {isSplitActive && (
                      <div className="splitAmountInput">
                        <span className="currencySymbol">₹</span>
                        <input
                          type="number"
                          className="amountInput"
                          {...register(`splitAmounts.${bank._id}`)}
                          placeholder="0.00"
                          step={0.1}
                          onWheel={(e) => e.currentTarget.blur()} 
                        />
                      </div>
                    )}
                    {!isSplitActive && (
                      <input
                        type="radio"
                        checked={isMethodSelected(bank._id)}
                        onChange={() => handleMethodSelection(bank._id)}
                        className="bankRadioInput"
                      />
                    )}
                  </div>
                </label>
              </div>
            ))}
          </div>

          <div
            className={`paymentOption ${isMethodSelected(PaymentMethodEnum.Cash) ? "selected" : ""}`}
          >
            <label className="optionLabel">
              <div className="optionInfo">
                <div className="optionText">
                  <span className="optionName">Cash</span>
                </div>
              </div>
              <div className="paymentRightSection">
                {isSplitActive && (
                  <div className="splitAmountInput">
                    <span className="currencySymbol">₹</span>
                    <input
                      type="number"
                      className="amountInput"
                      {...register(`splitAmounts.${PaymentMethodEnum.Cash}`)}
                      placeholder="0.00"
                      onWheel={(e) => e.currentTarget.blur()} 
                    />
                  </div>
                )}
                {!isSplitActive && (
                  <input
                    type="radio"
                    checked={isMethodSelected(PaymentMethodEnum.Cash)}
                    onChange={() => handleMethodSelection(PaymentMethodEnum.Cash)}
                    className="optionRadioInput"
                  />
                )}
              </div>
            </label>
          </div>
          <div className='breakLine'/>
          <div
            className={`paymentOption ${isMethodSelected(PaymentMethodEnum.Card) ? "selected" : ""}`}
          >
            <label className="optionLabel">
              <div className="optionInfo">
                <div className="optionText">
                  <span className="optionName">Debit / Credit / ATM Card</span>
                </div>
              </div>
              <div className="paymentRightSection">
                {isSplitActive && (
                  <div className="splitAmountInput">
                    <span className="currencySymbol">₹</span>
                    <input
                      type="number"
                      className="amountInput"
                      {...register(`splitAmounts.${PaymentMethodEnum.Card}`)}
                      placeholder="0.00"
                      onWheel={(e) => e.currentTarget.blur()} 
                    />
                  </div>
                )}
                {!isSplitActive && (
                  <input
                    type="radio"
                    checked={isMethodSelected(PaymentMethodEnum.Card)}
                    onChange={() => handleMethodSelection(PaymentMethodEnum.Card)}
                    className="optionRadioInput"
                  />
                )}
              </div>
            </label>
          </div>
          <div className='breakLine'/>
          <div
            className={`paymentOption ${isMethodSelected(PaymentMethodEnum.Other) ? "selected" : ""}`}
          >
            <label className="optionLabel">
              <div className="optionInfo">
                <div className="optionText">
                  <span className="optionName">Other</span>
                </div>
              </div>
              <div className="paymentRightSection">
                {isSplitActive && (
                  <div className="splitAmountInput">
                    <span className="currencySymbol">₹</span>
                    <input
                      type="number"
                      className="amountInput"
                      {...register(`splitAmounts.${PaymentMethodEnum.Other}`)}
                      placeholder="0.00"
                      onWheel={(e) => e.currentTarget.blur()} 
                    />
                  </div>
                )}
                {!isSplitActive && (
                  <input
                    type="radio"
                    checked={isMethodSelected(PaymentMethodEnum.Other)}
                    onChange={() => handleMethodSelection(PaymentMethodEnum.Other)}
                    className="optionRadioInput"
                  />
                )}
              </div>
            </label>
          </div>
        </>
      )}
    </div>
  );
};

function PaymentSummary({ isExpanded, toggleExpanded }) {
    const { watch, control } = useFormContext();
    const formValues = watch();
    const items = formValues.productList || [];
    const itemTotal = DecimalCalc.sum(
      items.map((item: SellerProductDetail & { quantity: number }) => {
        const purchasePrice = item?.price?.purchasePrice ?? 0;
        return decimal(purchasePrice).mul(item.quantity || 0);
      })
    ).toNumber();
    const totalBill = itemTotal;
    return (
        <div className="paymentSummaryCard">
            <div className="paymentSummary">
                <div className="summaryHeader">
                    <span>Total Bill</span>
                </div>
                <div className="summaryTotal">
                    <span>Total Amount Paid</span>
                    <div className="totalAmountInput">{currencyFormatter(totalBill)}</div>
                </div>
            </div>
        </div>
    );
}
